import { useEffect } from 'react';

import { isSuperAdminMode, isSupportAdminMode } from 'utils/user';

const ATTR = 'data-sensitive-copy-admins';
const TOOLTIP_CLASS = '__sensitive-copy-tooltip';
const COPY_LABEL = 'Click to copy';
const COPIED_LABEL = 'Copied!';

const STYLES = `
[${ATTR}] { cursor: pointer; }
.${TOOLTIP_CLASS} {
  position: fixed;
  z-index: 9999;
  background: #1f2937;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: 0;
  transition: opacity 0.12s;
}
.${TOOLTIP_CLASS}.visible { opacity: 1; }
.${TOOLTIP_CLASS}::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #1f2937;
}
.${TOOLTIP_CLASS}.below::after {
  top: auto;
  bottom: 100%;
  border-top: none;
  border-bottom: 5px solid #1f2937;
}
`;

export default function SensitiveCopyAdminsProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isDemoEnv = window.location.hostname === 'demo.app.getthera.com';
    const isAdmin = isSuperAdminMode || isSupportAdminMode;

    if (!isAdmin) return;

    // On demo environment, hide all sensitive fields for admins
    if (isDemoEnv) {
      const style = document.createElement('style');
      style.textContent = `[${ATTR}] { display: none !important; }`;
      document.head.appendChild(style);
      return () => {
        style.remove();
      };
    }

    // Admin + not demo: activate click-to-copy with portal-rendered tooltip
    const style = document.createElement('style');
    style.textContent = STYLES;
    document.head.appendChild(style);

    const tooltip = document.createElement('div');
    tooltip.className = TOOLTIP_CLASS;
    tooltip.textContent = COPY_LABEL;
    document.body.appendChild(tooltip);

    let currentTarget: HTMLElement | null = null;
    let copiedTarget: HTMLElement | null = null;
    let copiedTimeout: ReturnType<typeof setTimeout> | null = null;

    const positionTooltip = (target: HTMLElement) => {
      const rect = target.getBoundingClientRect();
      const tipRect = tooltip.getBoundingClientRect();
      const rawLeft = rect.left + rect.width / 2 - tipRect.width / 2;
      // Clamp to viewport on both horizontal edges
      const maxLeft = window.innerWidth - tipRect.width - 4;
      const left = Math.max(4, Math.min(rawLeft, maxLeft));
      // Flip below the target if there isn't enough room above the viewport
      const topAbove = rect.top - tipRect.height - 8;
      const top = topAbove < 4 ? rect.bottom + 8 : topAbove;
      tooltip.classList.toggle('below', topAbove < 4);
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    };

    const showTooltip = (target: HTMLElement, label: string) => {
      tooltip.textContent = label;
      positionTooltip(target);
      tooltip.classList.add('visible');
    };

    const hideTooltip = () => {
      tooltip.classList.remove('visible');
    };

    const handleMouseOver = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.(`[${ATTR}]`) as HTMLElement | null;
      if (!target || target === currentTarget) return;
      currentTarget = target;
      const label = copiedTimeout && target === copiedTarget ? COPIED_LABEL : COPY_LABEL;
      showTooltip(target, label);
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.(`[${ATTR}]`) as HTMLElement | null;
      if (!target) return;
      const related = e.relatedTarget as HTMLElement | null;
      if (related && target.contains(related)) return;
      currentTarget = null;
      hideTooltip();
    };

    const handleClick = (e: Event) => {
      const target = (e.target as HTMLElement).closest?.(`[${ATTR}]`) as HTMLElement | null;
      if (!target) return;

      e.stopPropagation();
      e.preventDefault();

      const copyValue = target.getAttribute('data-sensitive-copy-value') || target.textContent?.trim() || '';

      navigator.clipboard
        .writeText(copyValue)
        .then(() => {
          if (copiedTimeout) clearTimeout(copiedTimeout);
          copiedTarget = target;
          showTooltip(target, COPIED_LABEL);

          copiedTimeout = setTimeout(() => {
            copiedTimeout = null;
            copiedTarget = null;
            if (currentTarget) {
              showTooltip(currentTarget, COPY_LABEL);
            } else {
              hideTooltip();
            }
          }, 1200);
        })
        .catch((err) => console.error('Could not copy text: ', err));
    };

    const handleReposition = () => {
      if (currentTarget) positionTooltip(currentTarget);
    };

    document.addEventListener('mouseover', handleMouseOver, true);
    document.addEventListener('mouseout', handleMouseOut, true);
    document.addEventListener('click', handleClick, true);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      if (copiedTimeout) clearTimeout(copiedTimeout);
      style.remove();
      tooltip.remove();
      document.removeEventListener('mouseover', handleMouseOver, true);
      document.removeEventListener('mouseout', handleMouseOut, true);
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, []);

  return children;
}
